import {
  CalendarOutlined,
  CheckOutlined,
  DownloadOutlined,
  FieldTimeOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { getCompensationTypeTranslationKey } from '@/constants/compensationTypeI18n';
import useCurrency from '@/hooks/useCurrency';
import { useGetEmploymentTypeNameById } from '@/hooks/useEmploymentTypes';
import { SPACE_MD } from '@/theme/buildAntdTheme';
import IconLabelText from '@/UI/IconLabel/IconLabelText';
import { formatShortDate } from '@/utils/formatDate';
import { formatJobFeeFields } from '@/utils/formatJobFeeFields';
import getRecruiterFeeSummary, {
  getSalaryFeeBracketDetails,
} from '@/utils/getRecruiterFeeSummary';

import type { CompanyAgreement } from '@/types/company';
import type { JobFeeFormatSubject } from '@/utils/formatJobFeeFields';
import type { RecruiterFeeSummarySubject } from '@/utils/getRecruiterFeeSummary';

type RecruiterTermsFeeSectionProps = {
  agreementProvider: JobFeeFormatSubject &
    RecruiterFeeSummarySubject & {
      recruiter_company_agreement?: CompanyAgreement;
      fee_currency: string;
    };
};

const RecruiterTermsFeeSection = ({
  agreementProvider,
}: RecruiterTermsFeeSectionProps) => {
  const { t, i18n } = useTranslation();
  const currency = useCurrency(agreementProvider.fee_currency);
  const getEmploymentTypeNameById = useGetEmploymentTypeNameById();
  const recruiterFeeSummary = getRecruiterFeeSummary(
    t,
    currency,
    agreementProvider,
    false,
  );
  const extendedFeeTerms = formatJobFeeFields(agreementProvider, false);
  const salaryFeeBracketDetails = getSalaryFeeBracketDetails(
    t,
    currency,
    agreementProvider.recruiter_company_agreement?.salary_fee_brackets ||
      agreementProvider.company_agreement?.salary_fee_brackets,
  );

  const getCompensationTypeLabel = (type: string | undefined) => {
    const key = getCompensationTypeTranslationKey(type);
    return key ? t(key) : (type ?? null);
  };

  const agreement = agreementProvider.recruiter_company_agreement;

  return (
    <Row gutter={[SPACE_MD, SPACE_MD]}>
      {agreement?.accepted_date && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<CheckOutlined />}
            label={t('CONTRACT_STATUS_ACCEPTED')}
            value={
              <Typography.Text type="success">
                {formatShortDate(agreement.accepted_date, i18n.language)}
              </Typography.Text>
            }
          />
        </Col>
      )}
      {agreement?.declined_date && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<InfoCircleOutlined />}
            label={t('CONTRACT_STATUS_DECLINED')}
            value={
              <Typography.Text type="danger">
                {formatShortDate(agreement.declined_date, i18n.language)}
              </Typography.Text>
            }
          />
        </Col>
      )}
      {agreement?.compensation_type && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<InfoCircleOutlined />}
            label={t('COMPENSATION_TYPE')}
            value={getCompensationTypeLabel(agreement.compensation_type)}
          />
        </Col>
      )}
      {agreement?.fee_currency && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<WalletOutlined />}
            label={t('CURRENCY')}
            value={agreement.fee_currency}
          />
        </Col>
      )}
      {!!recruiterFeeSummary.recruiterFee && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<WalletOutlined />}
            label={t('FEE_DETAILS_FORM_LABEL')}
            value={
              recruiterFeeSummary.fallbackActive &&
              !recruiterFeeSummary.feeUnknown &&
              !recruiterFeeSummary.recruiterFee.includes('-') ? (
                <>
                  <Typography.Link href="https://help.hireport.io/en/articles/7973723-what-fee-can-i-expect-after-a-placement">
                    {t('TIERED_FEES')}
                  </Typography.Link>
                  : {recruiterFeeSummary.recruiterFee}
                </>
              ) : (
                recruiterFeeSummary.recruiterFee
              )
            }
          />
        </Col>
      )}
      {!!salaryFeeBracketDetails.length && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<WalletOutlined />}
            label={t('SALARY_FEE_BRACKETS')}
            value={salaryFeeBracketDetails.join('\n')}
          />
        </Col>
      )}
      {!!extendedFeeTerms.payment_terms &&
        Number(extendedFeeTerms.payment_terms) > 0 && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('JOB_PAYMENT_TERMS')}
              value={`${extendedFeeTerms.payment_terms} ${t('DAYS')}`}
            />
          </Col>
        )}
      {extendedFeeTerms.guarantee_period !== null &&
        (extendedFeeTerms.guarantee_period ?? 0) > 0 && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('GUARANTEE_PERIOD')}
              value={`${extendedFeeTerms.guarantee_period} ${t('DAYS')}`}
            />
          </Col>
        )}
      {!!extendedFeeTerms.restitution &&
        Number(extendedFeeTerms.restitution) > 0 && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('JOB_RESTITUTION')}
              value={`${extendedFeeTerms.restitution} ${t('PERCENTAGE')}`}
            />
          </Col>
        )}
      {!!extendedFeeTerms.replacement_time &&
        Number(extendedFeeTerms.replacement_time) > 0 && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('JOB_REPLACEMENT_TIME')}
              value={`${extendedFeeTerms.replacement_time} ${t('MONTHS')}`}
            />
          </Col>
        )}
      {extendedFeeTerms.exclusivity_for_replacement === true && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<FieldTimeOutlined />}
            label={t('JOB_EXCLUSIVITY_FOR_REPLACEMENT')}
            value={t('YES')}
          />
        </Col>
      )}
      {!!extendedFeeTerms.restitution_payment_terms &&
        Number(extendedFeeTerms.restitution_payment_terms) > 0 && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('JOB_RESTITUTION_PAYMENT_TERMS')}
              value={`${extendedFeeTerms.restitution_payment_terms} ${t(
                'DAYS',
              )}`}
            />
          </Col>
        )}
      {!!extendedFeeTerms.payment_terms_start && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<CalendarOutlined />}
            label={t('JOB_PAYMENT_TERMS_START')}
            value={
              extendedFeeTerms.payment_terms_start === 'After signing contract'
                ? t('AFTER_SIGNING_CONTRACT')
                : extendedFeeTerms.payment_terms_start ===
                    'After candidate start date'
                  ? t('AFTER_CANDIDATE_START_DATE')
                  : extendedFeeTerms.payment_terms_start
            }
          />
        </Col>
      )}
      {!!extendedFeeTerms.custom_restitution_agreement && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<CalendarOutlined />}
            label={t('JOB_CUSTOM_RESTITUTION_AGREEMENT')}
            value={extendedFeeTerms.custom_restitution_agreement}
          />
        </Col>
      )}
      {!!extendedFeeTerms.et_details_freelance &&
        extendedFeeTerms.et_details_freelance !== '-' && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('COMPANY_AGREEMENT_ET_DETAILS', {
                employmentType: getEmploymentTypeNameById('freelance'),
              })}
              value={extendedFeeTerms.et_details_freelance}
            />
          </Col>
        )}
      {!!extendedFeeTerms.et_details_ctp &&
        extendedFeeTerms.et_details_ctp !== '-' && (
          <Col xs={24} md={12}>
            <IconLabelText
              icon={<CalendarOutlined />}
              label={t('COMPANY_AGREEMENT_ET_DETAILS', {
                employmentType: getEmploymentTypeNameById('ctp'),
              })}
              value={extendedFeeTerms.et_details_ctp}
            />
          </Col>
        )}
      {!!extendedFeeTerms.et_gda_ctp && extendedFeeTerms.et_gda_ctp !== '-' && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<CalendarOutlined />}
            label={`${t(
              'COMPANY_AGREEMENT_ET_GDA_SHORT',
            )} (${getEmploymentTypeNameById('ctp')})`}
            value={extendedFeeTerms.et_gda_ctp}
          />
        </Col>
      )}
      {!!extendedFeeTerms.et_cao_ctp && extendedFeeTerms.et_cao_ctp !== '-' && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<CalendarOutlined />}
            label={`${t(
              'COMPANY_AGREEMENT_ET_CAO_SHORT',
            )} (${getEmploymentTypeNameById('ctp')})`}
            value={extendedFeeTerms.et_cao_ctp}
          />
        </Col>
      )}
      {!!extendedFeeTerms.other_information && (
        <Col xs={24} md={12}>
          <IconLabelText
            icon={<MessageOutlined />}
            label={t('JOB_OTHER_INFORMATION')}
            value={extendedFeeTerms.other_information}
          />
        </Col>
      )}
      {!!extendedFeeTerms.contract && (
        <Col xs={24}>
          <Button
            color="primary"
            variant="outlined"
            size="small"
            icon={<DownloadOutlined />}
            href={extendedFeeTerms.contract as string}
            target="_blank"
            rel="noreferrer"
          >
            {t('COMPANY_AGREEMENT_DOWNLOAD_CONTRACT')}
          </Button>
        </Col>
      )}
    </Row>
  );
};

export default RecruiterTermsFeeSection;
